import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  mintRoomKey,
  sameRoomPlacements,
  type MapEditorRoom,
} from './mapEditorRooms';
import type {
  MapLayoutRefusal,
  MapLayoutStandingRoom,
} from './mapEditorUnreal';

export interface MapEditorContextValue {
  // The draft. Every edit below is local to this author — nothing reaches Unreal, and
  // therefore nothing reaches other participants, until publishLayout is called.
  // Duplicates are allowed and meaningful (two galleries in a row), so this is a list.
  //
  // Each room carries a key identifying it as a placement rather than as an element, which is what
  // lets the same module appear twice and still address its own spots. Keys are minted once, when
  // the author adds the room, and travel with it through every edit below — see mapEditorRooms.
  rooms: MapEditorRoom[];
  appendRoom: (elementId: string) => void;
  removeRoomAt: (index: number) => void;
  moveRoom: (index: number, direction: -1 | 1) => void;
  clearChain: () => void;

  // Throws the draft away and goes back to the layout that is standing. The recovery path out of
  // Clear, which otherwise leaves an author with an empty list, a world Unreal has hidden from them,
  // and no record of what was there.
  revertDraft: () => void;
  // There is something standing to go back to, and the draft is not already it.
  canRevert: boolean;

  // The draft as it stood at the last publish. Null means nothing has been published
  // from this session yet.
  publishedRooms: MapEditorRoom[] | null;
  // Records a publish. Called by the inspector alongside the SPAWN_LAYOUT send.
  markPublished: (rooms: MapEditorRoom[]) => void;
  // Draft differs from what was last published — i.e. this author is looking at
  // something the other participants are not.
  isDirty: boolean;

  // Rooms Unreal reports as actually standing (CURRENT_GEOMETRY_LIST). This is what
  // everyone currently sees, which is shorter than the published draft when Unreal
  // refused a room for overlapping one already placed.
  //
  // Carries each room's key and, once its level has been read, the addressable id of every spot in
  // it. That is the authoritative account of which spot ids exist in the world: a republish whose
  // rooms are unchanged is answered by doing nothing, so keys reach the world only when the layout
  // itself does.
  builtRooms: MapLayoutStandingRoom[] | null;
  setBuiltRooms: (rooms: MapLayoutStandingRoom[] | null) => void;

  // Rooms the authority turned down in the same report, keyed by their position in the chain that
  // was published. This is the only thing that distinguishes "Unreal built fewer rooms than I asked
  // for" from "I asked for fewer rooms" — without it a refused room simply vanishes with no reason.
  refusals: MapLayoutRefusal[] | null;
  setRefusals: (refusals: MapLayoutRefusal[] | null) => void;

  // Adopts the layout saved on the branding record as the starting draft. Unlike the
  // CURRENT_GEOMETRY_LIST seed this survives a refresh and reaches a second author on
  // another machine, because it is the author's saved intent rather than a report of what
  // one Unreal instance happens to have standing right now.
  //
  // `scopeId` is the branding the rooms came out of. It is what makes this safe to call again:
  // a new scope replaces the draft outright, the same scope keeps the one-shot rule.
  //
  // `treatAsPublished` records the hydrated rooms as the published baseline. Only for a record
  // this hub had to rewrite on read — see the persistence bridge.
  hydrateFromPersisted: (
    rooms: MapEditorRoom[],
    scopeId: string,
    treatAsPublished?: boolean,
  ) => void;

  // True once the author has changed the chain themselves. A restored or seeded chain describes a
  // layout that already exists elsewhere, so it is not something to build locally — previewing it
  // duplicated whatever the server was already showing.
  hasAuthorEdits: boolean;
}

const MapEditorContext = createContext<MapEditorContextValue | null>(null);

/**
 * Reads a geometry report as a draft: the author's own rooms, each with the key it is standing under.
 *
 * The reported key is adopted verbatim rather than re-minted, even though UE may have stamped it
 * itself from the room's index. It is the key the spots in that room are addressed by right now, and
 * a fresh one would re-point every one of them for nothing.
 *
 * @param rooms - Standing rooms from CURRENT_GEOMETRY_LIST, caps already dropped.
 * @returns The same rooms as draft entries.
 */
const asDraftRooms = (rooms: MapLayoutStandingRoom[]): MapEditorRoom[] =>
  rooms.map((room) => ({ id: room.id, key: room.roomKey }));

/**
 * Holds the map-editor draft: the rooms this author has assembled and how they relate to what is
 * standing in the world.
 *
 * Mounted above the editor overlay, so the draft and the published baseline survive the overlay
 * being closed and a branding being switched.
 *
 * @param props.children - Anything reading the draft through useMapEditor.
 */
export const MapEditorProvider = ({ children }: { children: ReactNode }) => {
  const [rooms, setRooms] = useState<MapEditorRoom[]>([]);
  const [publishedRooms, setPublishedRooms] = useState<MapEditorRoom[] | null>(
    null,
  );
  const [builtRooms, setBuiltRoomsState] = useState<
    MapLayoutStandingRoom[] | null
  >(null);
  const [refusals, setRefusals] = useState<MapLayoutRefusal[] | null>(null);

  // Unreal reports the standing layout after any spawn or clear, including ones this
  // author did not make. The first such report is adopted as the starting draft so an
  // author opening the editor begins from what is actually built rather than an empty
  // list — but only once, and only while they have not started editing, so a report
  // arriving mid-edit can never overwrite work in progress.
  const hasSeededRef = useRef(false);
  const hasEditedRef = useRef(false);

  // Whether this author has published from this session. Until they have, CURRENT_GEOMETRY_LIST is
  // the only account of what is standing, so every report is allowed to set the baseline. Once they
  // publish, their own intent takes over — see setBuiltRooms and markPublished.
  const hasPublishedRef = useRef(false);

  // Mirrors hasEditedRef as state, because two things outside this provider have to react to it.
  //
  // A restored or seeded chain is not a draft — it is a description of a layout that already
  // exists elsewhere. Previewing it put the author's local copy on top of the rooms the server
  // was already showing them, which is the doubled geometry seen on entry. Only edits the author
  // actually made are worth building locally.
  const [hasAuthorEdits, setHasAuthorEdits] = useState(false);

  const setBuiltRooms = useCallback(
    (standingRooms: MapLayoutStandingRoom[] | null) => {
      setBuiltRoomsState(standingRooms);

      if (!standingRooms) {
        return;
      }

      // Recording what is standing is a different job from seeding the draft, and it must not be
      // gated on it. hydrateFromPersisted wins the seed almost every time — the branding fetch beats
      // a round trip through Unreal — and while both jobs shared hasSeededRef, the report could never
      // do the one thing only it is allowed to do. Every client that had not published in this
      // session therefore read a standing layout as an unsaved draft, and said so in the inspector.
      //
      // Not conditioned on publishedRooms being unset either: a first report of an empty world
      // (a server that restarted before anyone republished) is a legitimate baseline, and the report
      // that follows the entry republish has to be able to replace it.
      if (!hasPublishedRef.current) {
        setPublishedRooms(asDraftRooms(standingRooms));
      }

      if (hasSeededRef.current || hasEditedRef.current) {
        return;
      }
      hasSeededRef.current = true;
      setRooms(asDraftRooms(standingRooms));
    },
    [],
  );

  // Which branding the draft currently describes. Null until the first hydrate.
  const scopeRef = useRef<string | null>(null);

  // Same one-shot discipline as the geometry seed, and it shares hasSeededRef with it so the
  // two can never fight: whichever arrives first defines the starting draft, and the other is
  // ignored rather than overwriting it. Editing locks both out for the rest of the session.
  //
  // The one-shot rule is per branding, not per session. This provider is mounted above the editor
  // overlay so it outlives a configuration switch, and the switch republishes the world from the
  // newly activated record — so a draft still describing the previous configuration is not work in
  // progress, it is a stale list pointed at rooms that are no longer standing. Left in place it
  // read as unsaved changes, and Save wrote the old configuration's rooms into the new
  // configuration's record.
  //
  // Everything scoped to the old branding goes with it: the published baseline, the geometry report
  // and its refusals all describe a world that has just been replaced. Only the very first hydrate
  // defers to hasEditedRef — an author who added a room before the branding fetch resolved keeps it,
  // which is the case that rule was written for.
  //
  // Deliberately does NOT mark the layout published by default. The branding record holds the
  // author's last saved *intent*, which is not evidence that the world was ever built from it — a
  // save can be written while the spawn fails, and a server can be cleared or restarted afterwards.
  // Treating intent as proof disabled Save on a restored chain, leaving an author looking at rooms
  // they could not republish because the button was dead. Only CURRENT_GEOMETRY_LIST, which reports
  // what Unreal actually has standing, is normally allowed to mark a layout published.
  //
  // `treatAsPublished` is the one exception, and it is not about intent at all: it is for a record
  // this hub rewrote on read. The dropped row was never the author's edit, so surfacing it as their
  // unsaved change would be this hub reporting its own bookkeeping as their work — and where the
  // rewrite cannot be reconciled against the geometry report at all, it would say so on every entry,
  // forever. See dropTrailingCap.
  const hydrateFromPersisted = useCallback(
    (
      savedRooms: MapEditorRoom[],
      scopeId: string,
      treatAsPublished = false,
    ) => {
      const isNewScope = !!scopeRef.current && scopeRef.current !== scopeId;

      // Recorded before the early return, not after it: this call may well decline to reseed — the
      // geometry report usually wins the first seed — and the draft still has to know which
      // configuration it belongs to, or the *next* one is not recognisable as a different one and
      // the switch goes unnoticed exactly as it did before.
      scopeRef.current = scopeId;

      if (!isNewScope && (hasSeededRef.current || hasEditedRef.current)) {
        return;
      }

      hasSeededRef.current = true;
      setRooms(savedRooms);

      if (isNewScope) {
        hasEditedRef.current = false;
        setHasAuthorEdits(false);
        setBuiltRoomsState(null);
        setRefusals(null);
      }

      hasPublishedRef.current = treatAsPublished;
      setPublishedRooms(treatAsPublished ? [...savedRooms] : null);
    },
    [],
  );

  const editRooms = useCallback(
    (update: (prev: MapEditorRoom[]) => MapEditorRoom[]) => {
      hasEditedRef.current = true;
      setHasAuthorEdits(true);
      setRooms(update);
    },
    [],
  );

  // A key is minted here and nowhere else in the edit path: adding a room is the only edit that
  // creates a placement. Adding the same element twice therefore mints twice, which is the case
  // per-placement content exists for — two galleries in a row addressing their own walls.
  const appendRoom = useCallback(
    (elementId: string) => {
      if (!elementId) {
        return;
      }
      editRooms((prev) => [...prev, { id: elementId, key: mintRoomKey() }]);
    },
    [editRooms],
  );

  const removeRoomAt = useCallback(
    (index: number) => {
      editRooms((prev) => prev.filter((_, i) => i !== index));
    },
    [editRooms],
  );

  // Moves the room, key and all. Nothing is re-minted and nothing is re-derived from the new
  // position: a key that moved with its row is the entire point of having one, since the content
  // assigned to that placement has to move with it rather than staying at the index.
  const moveRoom = useCallback(
    (index: number, direction: -1 | 1) => {
      editRooms((prev) => {
        const target = index + direction;
        if (
          index < 0 ||
          index >= prev.length ||
          target < 0 ||
          target >= prev.length
        ) {
          return prev;
        }
        const next = [...prev];
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      });
    },
    [editRooms],
  );

  // Empties the draft only. The standing layout is untouched until the author saves —
  // at which point an empty draft publishes as a clear, since SPAWN_LAYOUT carries
  // clearExisting.
  const clearChain = useCallback(() => {
    editRooms(() => []);
  }, [editRooms]);

  // What reverting goes back to: what Unreal reports standing, falling back to the last published
  // intent only when no report has arrived yet. The two differ whenever a room was refused, and
  // going back to intent would restore rows that are not in the world — the opposite of matching it.
  const revertTarget = useMemo(
    () => (builtRooms ? asDraftRooms(builtRooms) : publishedRooms),
    [builtRooms, publishedRooms],
  );

  // Discards the draft and puts the standing layout back in the list.
  //
  // Needed because Clear is otherwise a one-way door: it empties the list, and Unreal hides the
  // published rooms for as long as any draft is up — so the author is left looking at an empty world
  // with no record of what they had and nothing to restore it from.
  //
  // Deliberately not routed through editRooms: clearing hasAuthorEdits is the point. It lets the
  // preview bridge take the local draft down and hand the real rooms back, instead of building a
  // second copy of geometry the server is already showing.
  const revertDraft = useCallback(() => {
    if (!revertTarget) {
      return;
    }
    hasEditedRef.current = false;
    setHasAuthorEdits(false);
    setRooms(revertTarget);
  }, [revertTarget]);

  const canRevert = !!revertTarget && !sameRoomPlacements(rooms, revertTarget);

  // Records what this author asked for, which from here on outranks what Unreal reports standing.
  // The two differ whenever a room was refused, and letting the report win there would re-raise
  // "unsaved" the instant a publish came back short — telling the author their change had not been
  // applied when it had been, as far as it could be. The refused rows say so individually instead.
  const markPublished = useCallback((publishedRoomList: MapEditorRoom[]) => {
    hasPublishedRef.current = true;
    setPublishedRooms([...publishedRoomList]);
  }, []);

  // Before the first publish there is nothing to compare against, so any non-empty
  // draft counts as unsaved. An empty draft that has never been published is not
  // dirty — there is nothing to send.
  // hasAuthorEdits matters in the no-baseline branch: emptying a restored chain leaves nothing to
  // compare and nothing in the list, which read as "clean" and disabled Save — so the author could
  // not publish the clear that would take the standing rooms down. Having edited at all is enough
  // to mean there is something to send.
  // A key-only difference counts, because it counts for UE: a chain re-keyed under the same elements
  // rebuilds and re-stamps every spot id in it, so it is a change the author has to be able to
  // publish — see sameRoomPlacements.
  const isDirty = publishedRooms
    ? !sameRoomPlacements(rooms, publishedRooms)
    : hasAuthorEdits || rooms.length > 0;

  const value = useMemo<MapEditorContextValue>(
    () => ({
      rooms,
      appendRoom,
      removeRoomAt,
      moveRoom,
      clearChain,
      revertDraft,
      canRevert,
      publishedRooms,
      markPublished,
      isDirty,
      builtRooms,
      setBuiltRooms,
      refusals,
      setRefusals,
      hydrateFromPersisted,
      hasAuthorEdits,
    }),
    [
      rooms,
      appendRoom,
      removeRoomAt,
      moveRoom,
      clearChain,
      revertDraft,
      canRevert,
      publishedRooms,
      markPublished,
      isDirty,
      builtRooms,
      setBuiltRooms,
      refusals,
      hydrateFromPersisted,
      hasAuthorEdits,
    ],
  );

  return (
    <MapEditorContext.Provider value={value}>
      {children}
    </MapEditorContext.Provider>
  );
};

/**
 * Reads the map-editor draft.
 *
 * @returns The draft rooms, the edit operations on them, and the two accounts of what is published —
 * the author's own intent and Unreal's report of what is standing.
 */
export const useMapEditor = (): MapEditorContextValue => {
  const ctx = useContext(MapEditorContext);
  if (!ctx) {
    throw new Error('useMapEditor must be used inside MapEditorProvider');
  }
  return ctx;
};
